// Device capability definitions for filtering unsupported commands
export type DeviceCapability = "position" | "vibration" | "rotation" | "oscillation" | "air" | "squeeze" | "surge" | "sway" | "roll" | "pitch"

export interface DeviceCapabilities {
  position: boolean
  vibration: boolean
  rotation: boolean
  oscillation: boolean
  air: boolean
  squeeze: boolean
  surge: boolean    // L1 - OSR2/SR6
  sway: boolean     // L2 - OSR2/SR6
  roll: boolean     // R1 - OSR2/SR6
  pitch: boolean    // R2 - OSR2/SR6
}

// Device capability map - defines what each device type supports
export const DEVICE_CAPABILITIES: Record<string, DeviceCapabilities> = {
  handy: {
    position: true,
    vibration: false,
    rotation: false,
    oscillation: false,
    air: false,
    squeeze: false,
    surge: false,
    sway: false,
    roll: false,
    pitch: false,
  },
  lovense: {
    position: false,
    vibration: true,
    rotation: true,
    oscillation: true,
    air: true,
    squeeze: false,
    surge: false,
    sway: false,
    roll: false,
    pitch: false,
  },
  kiiroo: {
    position: true,
    vibration: true,
    rotation: false,
    oscillation: false,
    air: false,
    squeeze: false,
    surge: false,
    sway: false,
    roll: false,
    pitch: false,
  },
  osr2: {
    position: true,
    vibration: true,
    rotation: true,
    oscillation: false,
    air: false,
    squeeze: false,
    surge: true,   // L1
    sway: true,    // L2
    roll: true,    // R1
    pitch: true,   // R2
  },
  sr6: {
    position: true,
    vibration: true,
    rotation: true,
    oscillation: false,
    air: false,
    squeeze: false,
    surge: true,   // L1
    sway: true,    // L2
    roll: true,    // R1
    pitch: true,   // R2
  },
  buttplug: {
    position: true,
    vibration: true,
    rotation: true,
    oscillation: true,
    air: true,
    squeeze: true,
    surge: true,
    sway: true,
    roll: true,
    pitch: true,
  },
  other: {
    position: true,
    vibration: true,
    rotation: false,
    oscillation: false,
    air: false,
    squeeze: false,
    surge: false,
    sway: false,
    roll: false,
    pitch: false,
  },
}

// The static map above is per device TYPE only, but Handy's vibration
// (HVP) is per API VERSION — v3-only, no v2 equivalent at all. Rather than
// make the whole map version-aware (every other device type/capability is
// genuinely fixed hardware, not API-version-gated), this overlays just that
// one exception on top of the static lookup.
export function getEffectiveCapabilities(deviceType: string, apiVersion?: "v2" | "v3"): DeviceCapabilities | undefined {
  const base = DEVICE_CAPABILITIES[deviceType]
  if (!base) return undefined
  if (deviceType === "handy" && apiVersion === "v3") {
    return { ...base, vibration: true }
  }
  return base
}

// Helper function to check if a device supports a capability
export function deviceSupportsCapability(deviceType: string, capability: DeviceCapability, apiVersion?: "v2" | "v3"): boolean {
  const capabilities = getEffectiveCapabilities(deviceType, apiVersion)
  if (!capabilities) return false
  return capabilities[capability]
}

// Helper to filter commands based on device capabilities
export interface DeviceCommand {
  position?: number
  vibration?: number
  rotation?: number
  oscillation?: number
  air?: number
  squeeze?: number
  surge?: number
  sway?: number
  roll?: number
  pitch?: number
}

export function filterDeviceCommands(deviceType: string, commands: DeviceCommand, apiVersion?: "v2" | "v3"): DeviceCommand {
  const filtered: DeviceCommand = {}
  const capabilities = getEffectiveCapabilities(deviceType, apiVersion)

  if (!capabilities) return commands

  if (commands.position !== undefined && capabilities.position) filtered.position = commands.position
  if (commands.vibration !== undefined && capabilities.vibration) filtered.vibration = commands.vibration
  if (commands.rotation !== undefined && capabilities.rotation) filtered.rotation = commands.rotation
  if (commands.oscillation !== undefined && capabilities.oscillation) filtered.oscillation = commands.oscillation
  if (commands.air !== undefined && capabilities.air) filtered.air = commands.air
  if (commands.squeeze !== undefined && capabilities.squeeze) filtered.squeeze = commands.squeeze
  if (commands.surge !== undefined && capabilities.surge) filtered.surge = commands.surge
  if (commands.sway !== undefined && capabilities.sway) filtered.sway = commands.sway
  if (commands.roll !== undefined && capabilities.roll) filtered.roll = commands.roll
  if (commands.pitch !== undefined && capabilities.pitch) filtered.pitch = commands.pitch

  return filtered
}
